import React, { Fragment } from 'react';
import { Lightbulb } from 'lucide-react';
import { cn } from '../../lib/utils';

const SKELETON_WIDTHS = ['w-[92%]', 'w-[78%]', 'w-[65%]'];

const BOLD_PATTERN = /(\*\*[^*]+\*\*)/g;

function renderInsightFinding(text) {
  if (!text || typeof text !== 'string') return null;

  return text.split(BOLD_PATTERN).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong
          key={index}
          className="font-semibold text-violet-900 dark:text-violet-100"
        >
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

const InsightSkeleton = () => (
  <ul className="space-y-2.5 px-3 py-2.5" aria-hidden>
    {SKELETON_WIDTHS.map((widthClass) => (
      <li key={widthClass} className="flex gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-300/70 dark:bg-violet-600/50" />
        <span
          className={cn(
            'h-3.5 animate-pulse rounded bg-violet-200/80 dark:bg-violet-800/40',
            widthClass
          )}
        />
      </li>
    ))}
  </ul>
);

const InsightBlock = ({ insight, loading = false }) => {
  const findings = Array.isArray(insight?.key_findings)
    ? insight.key_findings.filter(Boolean)
    : [];

  if (!loading && findings.length === 0) return null;

  return (
    <div
      className={cn(
        'mt-4 rounded-lg border border-violet-200/80 bg-violet-50/60',
        'dark:border-violet-800/50 dark:bg-violet-950/30'
      )}
      aria-busy={loading}
      aria-live="polite"
    >
      <div className="flex items-center gap-2 border-b border-violet-200/60 px-3 py-2 dark:border-violet-800/40">
        <Lightbulb
          size={14}
          className={cn(
            'shrink-0 text-violet-600 dark:text-violet-400',
            loading && 'animate-pulse'
          )}
          aria-hidden
        />
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          Insights
        </span>
      </div>
      {loading ? (
        <InsightSkeleton />
      ) : (
        <ul className="space-y-1.5 px-3 py-2.5">
          {findings.map((finding, index) => (
            <li
              key={`${index}-${finding}`}
              className="flex gap-2 text-sm text-gray-700 dark:text-gray-300"
            >
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500 dark:bg-violet-400"
                aria-hidden
              />
              <span>{renderInsightFinding(finding)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default InsightBlock;
